import {
    addDays,
    addHours,
    addMinutes,
    addMonths,
    addSeconds,
    addWeeks,
    addYears,
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    differenceInSeconds,
    differenceInWeeks,
    differenceInYears,
    format,
    formatDistanceToNow,
    subDays,
    subHours,
    subMinutes,
    subMonths,
    subSeconds,
    subWeeks,
    subYears
} from 'date-fns';

type TimeUnit = 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years';

type DiffMethod = (laterDate: Date, earlierDate: Date) => number;
const TIME_UNIT_TO_DIFF_METHOD: Record<TimeUnit, DiffMethod> = {
    seconds: differenceInSeconds,
    minutes: differenceInMinutes,
    hours: differenceInHours,
    days: differenceInDays,
    weeks: differenceInWeeks,
    months: differenceInMonths,
    years: differenceInYears
};

export const getDiffBetweenDates = (firstDate: Date, secondDate: Date, timeUnit: TimeUnit): number => {
    return Math.abs(TIME_UNIT_TO_DIFF_METHOD[timeUnit](secondDate, firstDate));
};

export const getDateDiffFromNow = (date: Date, timeUnit: TimeUnit): number => {
    return getDiffBetweenDates(date, new Date(), timeUnit);
};

export const getDateShortFormat = (date: Date): string => {
    return format(date, 'LLL dd, yyyy');
};

type AddMethod = (date: Date, amount: number) => Date;
const TIME_UNIT_TO_ADD_METHOD: Record<TimeUnit, AddMethod> = {
    seconds: addSeconds,
    minutes: addMinutes,
    hours: addHours,
    days: addDays,
    weeks: addWeeks,
    months: addMonths,
    years: addYears
};

export const addToDate = (date: Date, timeUnit: TimeUnit, amount: number): Date => {
    return TIME_UNIT_TO_ADD_METHOD[timeUnit](date, amount);
};

export enum DateFormat {
    ISO_DATE = 'yyyy-MM-dd',
    ISO_DATETIME = 'yyyy-MM-dd HH:mm:ss',
    READABLE_DATE = 'dd MMM yyyy',
    READABLE_DATETIME = 'dd MMM yyyy HH:mm',
    SHORT_DATE_MD = 'MMM dd, yyyy',
    LONG_DATE_MD = 'MMM dd, yyyy | h:mmaa',
    FILE = "yyyy-MM-dd'T'HH:mm:ssXXX"
}

export const convertUTCToLocal = (utcDate: Date): Date => {
    return new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);
};

export const formatDate = (date: Date, dateFormat: DateFormat, utcToLocal = false): string => {
    const dateToFormat = utcToLocal ? convertUTCToLocal(date) : date;
    return format(dateToFormat, dateFormat);
};

export const formatDateToNow = (date: Date): string => {
    return formatDistanceToNow(date, { addSuffix: true });
};

type SubtractMethod = (date: Date, amount: number) => Date;

const TIME_UNIT_TO_SUBTRACT_METHOD: Record<TimeUnit, SubtractMethod> = {
    seconds: subSeconds,
    minutes: subMinutes,
    hours: subHours,
    days: subDays,
    weeks: subWeeks,
    months: subMonths,
    years: subYears
};

export const subtractFromDate = (date: Date, value: number, timeUnit: TimeUnit): Date => {
    return TIME_UNIT_TO_SUBTRACT_METHOD[timeUnit](date, value);
};

const getStartOfMinute = (date: Date): Date => {
    return new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes())
    );
};

const getStartOfHour = (date: Date): Date => {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours()));
};

const getStartOfDay = (date: Date): Date => {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
};

const getStartOfWeek = (date: Date): Date => {
    const day = date.getUTCDay();
    const diff = day === 0 ? 6 : day - 1;
    const startOfWeek = new Date(date);
    startOfWeek.setUTCDate(date.getUTCDate() - diff);
    return getStartOfDay(startOfWeek);
};

const getStartOfMonth = (date: Date): Date => {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth()));
};

const getStartOfYear = (date: Date): Date => {
    return new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
};

const getEndOfMinute = (date: Date): Date => {
    return new Date(
        Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            date.getUTCHours(),
            date.getUTCMinutes(),
            59,
            999
        )
    );
};

const getEndOfHour = (date: Date): Date => {
    return new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), 59, 59, 999)
    );
};

const getEndOfDay = (date: Date): Date => {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999));
};

const getEndOfWeek = (date: Date): Date => {
    const day = date.getUTCDay();
    const diff = day === 0 ? 0 : 7 - day;
    const endOfWeek = new Date(date);
    endOfWeek.setUTCDate(date.getUTCDate() + diff);
    return getEndOfDay(endOfWeek);
};

const getEndOfMonth = (date: Date): Date => {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 23, 59, 59, 999));
};

const getEndOfYear = (date: Date): Date => {
    return new Date(Date.UTC(date.getUTCFullYear(), 11, 31, 23, 59, 59, 999));
};

type StartOfMethod = (date: Date) => Date;
const TIME_UNIT_TO_START_OF_METHOD: Record<TimeUnit, StartOfMethod> = {
    seconds: getStartOfMinute,
    minutes: getStartOfMinute,
    hours: getStartOfHour,
    days: getStartOfDay,
    weeks: getStartOfWeek,
    months: getStartOfMonth,
    years: getStartOfYear
};

type EndOfMethod = (date: Date) => Date;
const TIME_UNIT_TO_END_OF_METHOD: Record<TimeUnit, EndOfMethod> = {
    seconds: getEndOfMinute,
    minutes: getEndOfMinute,
    hours: getEndOfHour,
    days: getEndOfDay,
    weeks: getEndOfWeek,
    months: getEndOfMonth,
    years: getEndOfYear
};

export const getStartOf = (date: Date, timeUnit: TimeUnit): Date => {
    return TIME_UNIT_TO_START_OF_METHOD[timeUnit](date);
};

export const getEndOf = (date: Date, timeUnit: TimeUnit): Date => {
    return TIME_UNIT_TO_END_OF_METHOD[timeUnit](date);
};
